import React, { useState, useEffect, useCallback } from 'react';
import { fetchCourses, fetchDepartments } from './catalogApi';

function Catalog() {
  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchCourses({ search: committedSearch, department, page, perPage: 50 }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [committedSearch, department, page]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setCommittedSearch(searchInput.trim());
  };

  const onDepartment = (e) => {
    setPage(1);
    setDepartment(e.target.value);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Catalog</h2>
        <p className="text-sm text-gray-500">
          Browse the loaded course catalog for the selected institution. Switch institution with the
          selector above.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search code or title…"
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-64"
          />
          <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Search
          </button>
        </form>
        <select value={department} onChange={onDepartment} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md">
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.code}>{d.code} — {d.name}</option>
          ))}
        </select>
        {data && <span className="text-sm text-gray-500">{data.total} course{data.total === 1 ? '' : 's'}</span>}
      </div>

      {error && <div className="text-red-600 text-sm">Error: {error}</div>}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading catalog…</div>
      ) : data && data.courses.length === 0 ? (
        <div className="bg-white rounded-lg border p-6 text-gray-500 text-sm">No courses match.</div>
      ) : data ? (
        <>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs bg-gray-50">
                  <th className="px-3 py-2 font-medium">Code</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Dept</th>
                  <th className="px-3 py-2 font-medium">Cr</th>
                  <th className="px-3 py-2 font-medium">Lvl</th>
                </tr>
              </thead>
              <tbody>
                {data.courses.map((c) => (
                  <tr key={`${c.department_code}-${c.code}`} className="border-t border-gray-50 align-top">
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{c.code}</td>
                    <td className="px-3 py-2">
                      <div className="text-gray-800">{c.title}</div>
                      {c.prerequisites_raw && (
                        <div className="text-xs text-gray-400 mt-0.5">prereq: {c.prerequisites_raw}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{c.department_code || '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{c.credits}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-500">{c.level ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 text-sm">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
                className="px-3 py-1.5 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-500">Page {data.page} of {data.pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={data.page >= data.pages}
                className="px-3 py-1.5 bg-gray-100 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default Catalog;
